import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Button, Table, Badge, Modal, Alert, Spinner } from 'react-bootstrap';
import apiClient from '../../services/apiClient';

type Status = 'active' | 'inactive' | 'lost' | 'reissued';
type VehicleType = 'car' | 'motorcycle' | 'truck' | 'suv' | 'van' | 'other';

interface ETagItem {
  _id: string;
  tagId: string;
  licensePlate?: string;
  ownerName?: string;
  vehicleType?: VehicleType;
  vehicleDescription?: string;
  status: Status;
  authorization?: {
    enabled: boolean;
    zones: string[];
    visitor?: { isTemporary: boolean; expiresAt?: string };
    timeWindows?: { start: string; end: string }[];
  };
  registeredAt?: string;
  updatedAt?: string;
  usageCount?: number;
}

const statusColors: Record<Status, string> = {
  active: 'success',
  inactive: 'secondary',
  lost: 'warning',
  reissued: 'info'
};

const ETagManagement: React.FC = () => {
  const [items, setItems] = useState<ETagItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | Status>('');
  const [zone, setZone] = useState('');
  const [isTemporary, setIsTemporary] = useState<'all' | 'true' | 'false'>('all');

  // create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    tagId: '',
    licensePlate: '',
    ownerName: '',
    vehicleType: 'car' as VehicleType,
    status: 'active' as Status,
    zones: '' as string,
    visitorIsTemporary: false,
    visitorExpiresAt: ''
  });
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page, limit: 20 };
      if (search) params.search = search;
      if (status) params.status = status;
      if (zone) params.zone = zone;
      if (isTemporary !== 'all') params.isTemporary = isTemporary === 'true';
      const res = await apiClient.get('/etags', { params });
      if (res.data?.success) {
        setItems(res.data.data || []);
        setTotalPages(res.data.pagination?.pages || 1);
      } else {
        setError(res.data?.message || '查詢失敗');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || '查詢失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [page]);

  const resetFilters = () => {
    setSearch('');
    setStatus('');
    setZone('');
    setIsTemporary('all');
    setPage(1);
  };

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const zones = createForm.zones.split(',').map(z => z.trim()).filter(Boolean);
      const payload: any = {
        tagId: createForm.tagId,
        licensePlate: createForm.licensePlate || undefined,
        ownerName: createForm.ownerName || undefined,
        vehicleType: createForm.vehicleType,
        status: createForm.status,
        authorization: {
          enabled: true,
          zones,
          visitor: {
            isTemporary: !!createForm.visitorIsTemporary,
            expiresAt: createForm.visitorIsTemporary && createForm.visitorExpiresAt ? new Date(createForm.visitorExpiresAt).toISOString() : undefined
          }
        }
      };
      const res = await apiClient.post('/etags', payload);
      if (res.data?.success) {
        setShowCreate(false);
        setCreateForm({ tagId: '', licensePlate: '', ownerName: '', vehicleType: 'car', status: 'active', zones: '', visitorIsTemporary: false, visitorExpiresAt: '' });
        await fetchData();
      } else {
        setError(res.data?.message || '建立失敗');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || '建立失敗');
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (id: string, newStatus: Status) => {
    setError(null);
    try {
      await apiClient.post(`/etags/${id}/status`, { status: newStatus });
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || '更新狀態失敗');
    }
  };

  const statusOptions: { value: Status; label: string }[] = useMemo(() => ([
    { value: 'active', label: '啟用' },
    { value: 'inactive', label: '停用' },
    { value: 'lost', label: '掛失' },
    { value: 'reissued', label: '補發' }
  ]), []);

  return (
    <div>
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">e-Tag 管理</h5>
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            <i className="fas fa-plus me-2" /> 新增 e-Tag
          </Button>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
          <Row className="gy-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>關鍵字（TagID/車牌/車主）</Form.Label>
                <Form.Control value={search} onChange={(e) => setSearch(e.target.value)} placeholder="輸入關鍵字..." />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>狀態</Form.Label>
                <Form.Select value={status} onChange={(e) => setStatus(e.target.value as Status | '')}>
                  <option value="">全部</option>
                  {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>授權區域</Form.Label>
                <Form.Control value={zone} onChange={(e) => setZone(e.target.value)} placeholder="例如：A區" />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>臨時訪客</Form.Label>
                <Form.Select value={isTemporary} onChange={(e) => setIsTemporary(e.target.value as 'all' | 'true' | 'false')}>
                  <option value="all">全部</option>
                  <option value="true">是</option>
                  <option value="false">否</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <div className="mt-3 d-flex gap-2">
            <Button variant="primary" onClick={() => { setPage(1); fetchData(); }}>
              <i className="fas fa-search me-2" /> 查詢
            </Button>
            <Button variant="outline-secondary" onClick={resetFilters}>
              <i className="fas fa-times me-2" /> 清除
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h6 className="mb-0">e-Tag 列表</h6>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-tags fa-3x text-muted mb-3" />
              <p className="text-muted">沒有找到符合條件的 e-Tag</p>
            </div>
          ) : (
            <Table responsive striped hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th>e-Tag ID</th>
                  <th>車牌號碼</th>
                  <th>車主姓名</th>
                  <th>車輛型態</th>
                  <th>狀態</th>
                  <th>授權區域</th>
                  <th>臨時訪客</th>
                  <th>使用次數</th>
                  <th>更新時間</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item._id}>
                    <td>{item.tagId}</td>
                    <td>{item.licensePlate || '-'}</td>
                    <td>{item.ownerName || '-'}</td>
                    <td>{item.vehicleType || '-'}</td>
                    <td>
                      <Badge bg={statusColors[item.status] || 'secondary'}>{
                        item.status === 'active' ? '啟用' : item.status === 'inactive' ? '停用' : item.status === 'lost' ? '掛失' : '補發'
                      }</Badge>
                    </td>
                    <td>{(item.authorization?.zones || []).join(', ') || '-'}</td>
                    <td>{item.authorization?.visitor?.isTemporary ? `是（${item.authorization?.visitor?.expiresAt ? new Date(item.authorization!.visitor!.expiresAt!).toLocaleString() : '未設定到期'}）` : '否'}</td>
                    <td>{item.usageCount ?? 0}</td>
                    <td>{item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '-'}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button size="sm" variant="outline-success" onClick={() => updateStatus(item._id, 'active')}>啟用</Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => updateStatus(item._id, 'inactive')}>停用</Button>
                        <Button size="sm" variant="outline-warning" onClick={() => updateStatus(item._id, 'lost')}>掛失</Button>
                        <Button size="sm" variant="outline-info" onClick={() => updateStatus(item._id, 'reissued')}>補發</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between align-items-center">
          <div>第 {page} / {totalPages} 頁</div>
          <div className="d-flex gap-2">
            <Button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>上一頁</Button>
            <Button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>下一頁</Button>
          </div>
        </Card.Footer>
      </Card>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>新增 e-Tag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>e-Tag ID</Form.Label>
              <Form.Control value={createForm.tagId} onChange={(e) => setCreateForm({ ...createForm, tagId: e.target.value })} />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>車牌號碼</Form.Label>
                  <Form.Control value={createForm.licensePlate} onChange={(e) => setCreateForm({ ...createForm, licensePlate: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>車主姓名</Form.Label>
                  <Form.Control value={createForm.ownerName} onChange={(e) => setCreateForm({ ...createForm, ownerName: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>車輛型態</Form.Label>
                  <Form.Select value={createForm.vehicleType} onChange={(e) => setCreateForm({ ...createForm, vehicleType: e.target.value as VehicleType })}>
                    <option value="car">汽車</option>
                    <option value="motorcycle">機車</option>
                    <option value="truck">卡車</option>
                    <option value="suv">SUV</option>
                    <option value="van">廂型車</option>
                    <option value="other">其他</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>狀態</Form.Label>
                  <Form.Select value={createForm.status} onChange={(e) => setCreateForm({ ...createForm, status: e.target.value as Status })}>
                    {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>授權區域（以逗號分隔）</Form.Label>
              <Form.Control value={createForm.zones} onChange={(e) => setCreateForm({ ...createForm, zones: e.target.value })} placeholder="例如：A區,B區" />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="臨時訪客"
                    checked={createForm.visitorIsTemporary}
                    onChange={(e) => setCreateForm({ ...createForm, visitorIsTemporary: e.target.checked })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>到期時間（選填）</Form.Label>
                  <Form.Control type="datetime-local" value={createForm.visitorExpiresAt} onChange={(e) => setCreateForm({ ...createForm, visitorExpiresAt: e.target.value })} disabled={!createForm.visitorIsTemporary} />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>取消</Button>
          <Button variant="primary" onClick={handleCreate} disabled={creating}>
            {creating ? <Spinner size="sm" animation="border" className="me-2" /> : null}
            建立
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ETagManagement;